import React, { useCallback, useEffect, useState, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useForm, useFieldArray } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { Box, Divider, LinearProgress, Pagination } from '@mui/material';
import { KeyboardArrowUp, KeyboardArrowDown } from '@mui/icons-material';

import {
    Container,
    ErrorMessage,
    SimpleSingleSelect,
    SimpleMultipleSelect,
    SimpleTextField,
    Loading,
    SimpleButton,
    Tooltip,
} from '@/src/ui';
import { getZeroOption, type Option } from '@/src/utils';
import { apiRequest } from '@/src/services';
import {
    QUANTITY_OPTIONS,
    type FilterOptions,
    type QuestionTypeOption,
    type RequestParams,
    type QuestionAndIdDTO,
    type ConfigSet,
    type DynamicQuizFormValues,
    type QuestionGenerationDTO,
    type QuestionVariant,
    type QuizGetResponseDTO,
    schema,
} from './dtos';
import { QuestionRenderer } from '@/src/lib';
import SaveQuizModal from './components/SaveQuizModal';
import { convertBase64ImagesInQuestion } from './services/quizImageProcessing';

const NoStandardsFound = getZeroOption('No standards found for the selected grades');
const SelectStandardOption = getZeroOption('Select a standard');
const NoVariantsOption = getZeroOption('No variants available');

const QUESTIONS_PER_PAGE = 10;

const defaultConfigSet: ConfigSet = {
    standardOptionId: 0,
    variantIds: [],
    quantity: 0,
};

const DynamicQuizGeneratorPage: React.FC = () => {
    document.title = 'Quiz Generator';

    const [searchParams] = useSearchParams();
    const viewMode = searchParams.get('viewMode') as 'edit' | null;
    const resourceId = searchParams.get('resourceId')
        ? Number(searchParams.get('resourceId'))
        : null;

    const [loading, setLoading] = useState(false);
    const [editLoading, setEditLoading] = useState(viewMode === 'edit' && !!resourceId);
    const [error, setError] = useState('');
    const [questions, setQuestions] = useState<QuestionGenerationDTO[]>([]);

    const [gradeOptions, setGradeOptions] = useState<Option[]>([]);
    const [standardOptions, setStandardOptions] = useState<Record<number, QuestionTypeOption[]>>(
        {}
    );

    const [currentPage, setCurrentPage] = useState(1);
    const [showSaveModal, setShowSaveModal] = useState(false);

    const {
        control,
        handleSubmit,
        watch,
        setValue,
        getValues,
        formState: { errors },
    } = useForm<DynamicQuizFormValues>({
        defaultValues: {
            intendedGrades: [],
            title: '',
            standardGrades: [],
            config: [{ ...defaultConfigSet }],
        },
        resolver: yupResolver(schema as any),
        mode: 'onBlur',
    });

    const { fields, append, remove } = useFieldArray({
        control,
        name: 'config',
    });

    const standardGrades = watch('standardGrades');
    const config = watch('config');

    useEffect(() => {
        (async function init() {
            try {
                const gradesRes = await apiRequest<FilterOptions>(
                    'GET',
                    `utility/filterData?getGradeLevelMaster=true&getStandard=true`
                );
                const newGradeOptions = gradesRes.data.gradeLevelMasterOptions;
                const standardOptions = gradesRes.data.standardOptions;
                setGradeOptions(newGradeOptions);

                const optionsRes = await apiRequest<Record<number, QuestionTypeOption[]>>(
                    'GET',
                    `assessments/questions/dynamic/options`
                );

                const enriched: Record<number, QuestionTypeOption[]> = {};
                Object.entries(optionsRes.data).forEach(([gradeId, options]) => {
                    enriched[Number(gradeId)] = options.map((option) => ({
                        ...option,
                        gradeLevelId: Number(gradeId),
                        standardIds: option.standards.reduce<number[]>((acc, standard) => {
                            const match = standardOptions.find((s) => s.name === standard);
                            if (match) {
                                acc.push(Number(match.id));
                            }
                            return acc;
                        }, []),
                        variants: option.variants.map((v: any) => ({
                            gradeLevelId: Number(gradeId),
                            questionTypeId: option.id,
                            variantId: v.id,
                            name: v.name,
                        })),
                    }));
                });
                setStandardOptions(enriched);

                if (viewMode === 'edit' && resourceId) {
                    const quizRes = await apiRequest<QuizGetResponseDTO>(
                        'GET',
                        `assessments/exams/quizzes/${resourceId}`
                    );
                    const data = quizRes.data;
                    setValue('title', data.title ?? '');
                    setValue('intendedGrades', data.intendedGrades ?? []);
                    for (const question of data.questions) {
                        question.variantsForRegeneration = [];
                        await convertBase64ImagesInQuestion(question);
                    }
                    setQuestions(data.questions ?? []);
                }
            } catch {
                setError('Failed to fetch data');
            } finally {
                setLoading(false);
                setEditLoading(false);
            }
        })();

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const availableStandards = useMemo(() => {
        const result: QuestionTypeOption[] = [];
        Object.entries(standardOptions).forEach(([gradeId, options]) => {
            if (!standardGrades.includes(Number(gradeId))) return;
            options.forEach(
                (option: QuestionTypeOption) => (option.name = option.standards.join(', '))
            );
            result.push(...options);
        });
        return result.sort((a, b) => a.name!.localeCompare(b.name!));
    }, [standardGrades, standardOptions]);

    const availableStandardOptions = useMemo(() => {
        const options: Option[] = availableStandards.map((standard) => ({
            id: standard.id,
            name: standard.name || `Unknown Standard ${standard.id}`,
        }));
        if (!availableStandards.length) {
            options.push(NoStandardsFound);
        } else {
            options.unshift(SelectStandardOption);
        }
        return options;
    }, [availableStandards]);

    const getVariantOptions = useCallback(
        (standardOptionId: number) => {
            const standardOption = availableStandards.find(
                (option) => option.id === standardOptionId
            );
            if (!standardOption) return [];
            const options: Option[] = standardOption.variants.map((variant) => ({
                id: variant.variantId,
                name: variant.name,
            }));
            if (!options.length) options.push(NoVariantsOption);
            return options;
        },
        [availableStandards]
    );

    const handleAddConfigSet = useCallback(() => {
        if (fields.length >= 5) return;
        append({ ...defaultConfigSet });
    }, [fields.length, append]);

    const [nQuestionsLoaded, setNQuestionsLoaded] = useState(0);
    const [nQuestionsTotal, setNQuestionsTotal] = useState(0);
    const generateAllQuestions = useCallback(
        async (data: DynamicQuizFormValues, replace: boolean) => {
            const requestData: RequestParams[] = [];
            data.config.forEach((configItem) => {
                const standardOption = availableStandards.find(
                    (option) => option.id === configItem.standardOptionId
                );
                if (!standardOption) return null;

                const variants: QuestionVariant[] = configItem.variantIds.flatMap((variantId) => {
                    const variant = standardOption.variants.find((v) => v.variantId === variantId);
                    if (!variant) {
                        return [];
                    }
                    return [variant];
                });

                for (let i = 0; i < configItem.quantity + 1; i++) {
                    requestData.push({
                        variants,
                        standards: standardOption.standardIds ?? [],
                    });
                }
            });

            if (requestData.length > 50) {
                setError('Cannot generate more than 50 questions at a time');
                return;
            }

            setError('');
            setLoading(true);
            let newQuestions: QuestionGenerationDTO[] = replace ? [] : [...questions];
            setCurrentPage(1);
            setNQuestionsLoaded(0);
            setNQuestionsTotal(requestData.length);

            for (const request of requestData) {
                if (newQuestions.length >= 50) {
                    break;
                }
                const variant =
                    request.variants[Math.floor(Math.random() * request.variants.length)];
                try {
                    const res = await apiRequest<QuestionAndIdDTO>(
                        'GET',
                        `assessments/questions/dynamic/generate/${variant.gradeLevelId}/${variant.questionTypeId}/${variant.variantId}`
                    );
                    const newQuestion = res.data.question;
                    newQuestion.meta.standards = request.standards;
                    await convertBase64ImagesInQuestion(newQuestion);
                    const newQuestionDTO: QuestionGenerationDTO = {
                        ...newQuestion,
                        variantsForRegeneration: request.variants,
                    };
                    newQuestions = [...newQuestions, newQuestionDTO].splice(0, 50);
                    setNQuestionsLoaded((prev) => prev + 1);
                } catch (e) {
                    console.error(e);
                    setError('Failed to fetch questions');
                    break;
                }
            }
            setQuestions(newQuestions);
            setLoading(false);
        },
        [availableStandards, questions]
    );
    const handleGenerateClick = useCallback(
        (replace: boolean) => {
            if (questions.length > 0) {
                const windowMessage = replace
                    ? 'This will REPLACE your existing questions. Continue?'
                    : 'This will ADD to your existing questions. Questions will only generate until the limit of 50 is reached. Continue?';
                const confirmed = window.confirm(windowMessage);
                if (!confirmed) return;
            }
            handleSubmit((data) => generateAllQuestions(data, replace))();
        },
        [questions.length, handleSubmit, generateAllQuestions]
    );

    const [loadingSingleIndex, setLoadingSingleIndex] = useState<number | null>(null);
    const regenerateSingleQuestion = useCallback(
        async (index: number, question: QuestionGenerationDTO) => {
            setLoadingSingleIndex(index);
            const variant =
                question.variantsForRegeneration[
                    Math.floor(Math.random() * question.variantsForRegeneration.length)
                ];
            try {
                const res = await apiRequest<QuestionAndIdDTO>(
                    'GET',
                    `assessments/questions/dynamic/generate/${variant.gradeLevelId}/${variant.questionTypeId}/${variant.variantId}`
                );
                const newQuestion = res.data.question;
                await convertBase64ImagesInQuestion(newQuestion);
                const newQuestionDTO: QuestionGenerationDTO = {
                    ...newQuestion,
                    variantsForRegeneration: question.variantsForRegeneration,
                };
                setQuestions((prev) => {
                    const newQuestions = [...prev];
                    newQuestions[index] = newQuestionDTO;
                    return newQuestions;
                });
                setNQuestionsLoaded(1);
            } catch {
                setError('Failed to regenerate question');
            } finally {
                setLoadingSingleIndex(null);
            }
        },
        []
    );

    const removeSingleQuestion = useCallback((index: number) => {
        setQuestions((prev) => {
            const newQuestions = [...prev];
            newQuestions.splice(index, 1);
            return newQuestions;
        });
        setNQuestionsTotal((prev) => prev - 1);
    }, []);

    const moveQuestion = useCallback((index: number, direction: -1 | 1) => {
        setQuestions((prev) => {
            const target = index + direction;
            if (target < 0 || target >= prev.length) return prev;
            const newQuestions = [...prev];
            [newQuestions[index], newQuestions[target]] = [
                newQuestions[target],
                newQuestions[index],
            ];
            return newQuestions;
        });
    }, []);

    // Pagination derived state
    const totalPages = Math.max(1, Math.ceil(questions.length / QUESTIONS_PER_PAGE));
    const paginatedQuestions = useMemo(() => {
        const start = (currentPage - 1) * QUESTIONS_PER_PAGE;
        return questions.slice(start, start + QUESTIONS_PER_PAGE);
    }, [questions, currentPage]);

    // Clamp page if total drops below current
    useEffect(() => {
        if (currentPage > totalPages) {
            setCurrentPage(totalPages);
        }
    }, [totalPages, currentPage]);

    const checkFieldsAndOpenSaveModal = useCallback(() => {
        setError('');
        if (!questions.length) {
            setError('No questions to save');
            return;
        }
        const values = getValues();
        if (!values.intendedGrades?.length) {
            setError('Please select at least one intended grade before saving');
            return;
        }
        if (!values.title?.trim()) {
            setError('Please enter a quiz title before saving');
            return;
        }
        setShowSaveModal(true);
    }, [questions.length, getValues]);

    if (editLoading) {
        return (
            <Container>
                <h1 className="page-header">Quiz Generator</h1>
                <br />
                <Loading message="Loading quiz..." />
            </Container>
        );
    }

    return (
        <>
            <Container>
                <h1 className="page-header">Quiz Generator</h1>
                <br />
                <Box sx={{ maxWidth: 750 }}>
                    <h3>Quiz Settings</h3>
                    <br />
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'row',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            mb: 2,
                            gap: 2,
                            flexWrap: 'wrap',
                        }}
                    >
                        <SimpleMultipleSelect
                            sx={{ flex: 1 }}
                            name="intendedGrades"
                            label="Intended Grade Level(s)"
                            options={gradeOptions}
                            value={watch('intendedGrades')}
                            onChange={(val) => setValue('intendedGrades', val)}
                        />
                        <SimpleTextField
                            sx={{ flex: 1 }}
                            name="title"
                            label="Quiz Title"
                            value={watch('title')}
                            onChange={(e) => setValue('title', e.target.value)}
                        />
                    </Box>
                    <ErrorMessage error={errors.intendedGrades?.message} />
                    <ErrorMessage error={error} />

                    <Divider sx={{ my: 2 }} />

                    <h3>Question Generation</h3>
                    <br />
                    <Box sx={{ mb: 2, maxWidth: 500 }}>
                        <SimpleMultipleSelect
                            sx={{ flex: 1, width: '100%' }}
                            name="standardGrades"
                            label="Standard Grade(s)"
                            options={gradeOptions}
                            value={watch('standardGrades')}
                            onChange={(val) => setValue('standardGrades', val)}
                        />
                    </Box>
                    {fields.map((field, index) => {
                        if (!standardGrades.length) {
                            return null;
                        }
                        return (
                            <Box key={field.id} sx={{ mb: 2 }}>
                                <Box
                                    sx={{
                                        display: 'flex',
                                        flexDirection: 'row',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        gap: 2,
                                        flexWrap: 'wrap',
                                    }}
                                >
                                    <SimpleSingleSelect
                                        sx={{
                                            flex: 1,
                                            width: '100%',
                                            '& .MuiSelect-select': { width: '100%' },
                                        }}
                                        name={`config.${index}.standardOptionId`}
                                        label="Standard"
                                        options={availableStandardOptions}
                                        value={config[index]?.standardOptionId ?? 0}
                                        onChange={(e) => {
                                            setValue(
                                                `config.${index}.standardOptionId`,
                                                Number(e.target.value)
                                            );
                                            setValue(`config.${index}.variantIds`, []);
                                        }}
                                    />
                                    <SimpleMultipleSelect
                                        sx={{ flex: 1 }}
                                        name={`config.${index}.variantIds`}
                                        label="Variant"
                                        options={getVariantOptions(
                                            config[index]?.standardOptionId ?? 0
                                        )}
                                        value={config[index]?.variantIds ?? []}
                                        onChange={(val) =>
                                            setValue(`config.${index}.variantIds`, val)
                                        }
                                    />
                                    <SimpleSingleSelect
                                        sx={{ flex: 1 }}
                                        name={`config.${index}.quantity`}
                                        label="Quantity"
                                        options={QUANTITY_OPTIONS}
                                        value={config[index]?.quantity ?? 0}
                                        onChange={(e) =>
                                            setValue(
                                                `config.${index}.quantity`,
                                                Number(e.target.value)
                                            )
                                        }
                                    />
                                    <SimpleButton
                                        variant="outlined"
                                        color="error"
                                        onClick={() => remove(index)}
                                        sx={{
                                            visibility: index === 0 ? 'hidden' : 'visible',
                                            pointerEvents: index === 0 ? 'none' : 'auto',
                                        }}
                                    >
                                        Remove
                                    </SimpleButton>
                                </Box>
                                <ErrorMessage
                                    error={errors.config?.[index]?.standardOptionId?.message}
                                />
                                <ErrorMessage error={errors.config?.[index]?.variantIds?.message} />
                            </Box>
                        );
                    })}
                </Box>
                {standardGrades.length > 0 && (
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            mb: 2,
                        }}
                    >
                        <div>
                            {fields.length < 5 && (
                                <SimpleButton
                                    variant="contained"
                                    onClick={handleAddConfigSet}
                                    disabled={fields.length >= 5}
                                >
                                    Add Standard
                                </SimpleButton>
                            )}
                        </div>
                        {fields.length > 0 && (
                            <div style={{ display: 'flex', gap: 8 }}>
                                <SimpleButton
                                    variant="contained"
                                    color="secondary"
                                    disabled={
                                        loading || !questions.length || questions.length >= 50
                                    }
                                    onClick={() => handleGenerateClick(false)}
                                >
                                    Generate & Add
                                </SimpleButton>
                                <SimpleButton
                                    variant="contained"
                                    disabled={loading}
                                    onClick={() => handleGenerateClick(true)}
                                >
                                    Generate & Replace
                                </SimpleButton>
                            </div>
                        )}
                    </Box>
                )}
                {loading && (
                    <>
                        <div className="mb-2">
                            <LinearProgress
                                variant="determinate"
                                value={(nQuestionsLoaded * 100) / (nQuestionsTotal || 100)}
                            />
                        </div>
                        Loading questions ({nQuestionsLoaded} / {nQuestionsTotal})...
                    </>
                )}

                <Divider sx={{ my: 2 }} />

                {!loading &&
                    paginatedQuestions.map((question, pageIndex) => {
                        const index = (currentPage - 1) * QUESTIONS_PER_PAGE + pageIndex;
                        if (loadingSingleIndex === index) {
                            return <Loading key={`question-box-${index}`} />;
                        }
                        return (
                            <Box key={`question-box-${index}`} sx={{ mb: 4 }}>
                                <Box
                                    sx={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                    }}
                                >
                                    <span>Question {index + 1}</span>
                                    <div style={{ display: 'flex', gap: 8 }}>
                                        <SimpleButton
                                            color="warning"
                                            variant="contained"
                                            disabled={index === 0}
                                            onClick={() => moveQuestion(index, -1)}
                                        >
                                            <KeyboardArrowUp fontSize="small" />
                                        </SimpleButton>
                                        <SimpleButton
                                            color="warning"
                                            variant="contained"
                                            disabled={index === questions.length - 1}
                                            onClick={() => moveQuestion(index, 1)}
                                        >
                                            <KeyboardArrowDown fontSize="small" />
                                        </SimpleButton>
                                        {question.variantsForRegeneration.length === 0 ? (
                                            <Tooltip
                                                tooltipText="This question was saved in an old version of the quiz and cannot be regenerated."
                                                style={{ cursor: 'not-allowed' }}
                                            >
                                                <SimpleButton
                                                    onClick={() => null}
                                                    disabled
                                                    sx={{ cursor: 'not-allowed' }}
                                                >
                                                    Regenerate
                                                </SimpleButton>
                                            </Tooltip>
                                        ) : (
                                            <SimpleButton
                                                onClick={() =>
                                                    regenerateSingleQuestion(index, question)
                                                }
                                            >
                                                Regenerate
                                            </SimpleButton>
                                        )}
                                        <SimpleButton
                                            color="error"
                                            onClick={() => removeSingleQuestion(index)}
                                        >
                                            Remove
                                        </SimpleButton>
                                    </div>
                                </Box>
                                <QuestionRenderer
                                    key={`question-${index}`}
                                    question={question}
                                    stimMode="preview"
                                    options={{ showScoring: true }}
                                />
                            </Box>
                        );
                    })}
                <ErrorMessage error={error} />
                <ErrorMessage error={errors.config?.root?.message} />
            </Container>

            {questions.length > 0 && !loading && (
                <Box
                    sx={{
                        position: 'sticky',
                        bottom: 0,
                        backgroundColor: 'background.paper',
                        borderTop: '1px solid',
                        borderColor: 'divider',
                        // Match Container's responsive horizontal padding so contents
                        // align with the questions above. Tailwind px-4/6/10/20 maps
                        // to MUI spacing 2/3/5/10.
                        px: { xs: 2, sm: 3, md: 5, lg: 10 },
                        py: 2,
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        gap: 2,
                        zIndex: 10,
                    }}
                >
                    <Pagination
                        count={totalPages}
                        page={currentPage}
                        onChange={(_, page) => setCurrentPage(page)}
                        color="primary"
                    />
                    <SimpleButton variant="contained" onClick={() => checkFieldsAndOpenSaveModal()}>
                        Save Quiz
                    </SimpleButton>
                </Box>
            )}

            {showSaveModal && (
                <SaveQuizModal
                    open={showSaveModal}
                    title={watch('title')}
                    mode={viewMode === 'edit' ? 'edit' : 'create'}
                    editModeId={resourceId ?? undefined}
                    intendedGrades={watch('intendedGrades')}
                    standardGrades={watch('standardGrades')}
                    questions={questions}
                    onClose={() => setShowSaveModal(false)}
                    onSubmitCallback={() => null}
                />
            )}
        </>
    );
};

export default DynamicQuizGeneratorPage;
